import { Card, Skeleton } from "@attmos/flowstack-ui";

import { type Attestation, useRunProvenance } from "@/api/runs";
import { EmptyState } from "@/components/layout/EmptyState";

/** Attestation renders as an explicit tri-state badge (C0.2): an
 *  ``unattested`` run must never present as attested, so the state is
 *  always shown — including for the template-driven launches that stay
 *  unattested by design (D3). An unknown/absent value degrades to
 *  ``unattested``.
 *
 *  The attested state is labelled "Reported", not "Verified" (plan rev 3):
 *  the digest is measured host-side but reported to the controller by the
 *  container being measured, so it is provenance consistent with the pin,
 *  not independent attestation. The label must not overclaim.
 *
 *  Colour comes from the lifecycle-state trios rather than a raw Tailwind
 *  palette shade, which ``token-hygiene.test.ts`` bars outright and which
 *  carries no dark-theme story of its own. Attestation is not a lifecycle
 *  state, but its three outcomes are the same good / warn / bad ladder
 *  ``FragmentSummaryTable``'s ok · partial · failed badge already spells
 *  with these tokens — so this reads as the same badge family rather than
 *  as a fourth green. */
const ATTESTATION_STYLES: Record<Attestation, { label: string; className: string }> = {
  reported: {
    label: "Reported",
    className: "border-state-completed-border bg-state-completed-bg text-state-completed-fg",
  },
  mismatch: {
    label: "Mismatch",
    className:
      "border-state-failed-scientific-border bg-state-failed-scientific-bg text-state-failed-scientific-fg",
  },
  unattested: {
    label: "Unattested",
    className:
      "border-state-failed-validation-border bg-state-failed-validation-bg text-state-failed-validation-fg",
  },
};

function AttestationBadge({ value }: { value: Attestation | undefined }): JSX.Element {
  const state: Attestation = value === "reported" || value === "mismatch" ? value : "unattested";
  const { label, className } = ATTESTATION_STYLES[state];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${className}`}
    >
      {label}
    </span>
  );
}

export function RunProvenance({
  runId,
  isActive = false,
}: {
  runId: string;
  /** Poll while the run is in an active lifecycle state — outputs and
   *  git SHA land only when the run produces artifacts, so a mounted
   *  tab must pick them up (mirrors RunLogs' 5s active cadence). */
  isActive?: boolean;
}): JSX.Element {
  const { data, isLoading, isError } = useRunProvenance(runId, isActive);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
    );
  }

  // A transport/server failure is NOT "no provenance yet" — saying so
  // would misstate the cause and silently drop the attestation
  // surface. Name the failure; the record may exist. But only when
  // there is nothing to show: a failed BACKGROUND poll must not blank
  // out already-loaded provenance (Rupert PR-798 r2) — react-query
  // keeps the stale data, and stale lineage beats no lineage.
  if (isError && !data) {
    return (
      <EmptyState
        title="Could not load provenance."
        description="The provenance record could not be fetched. Retry, or check the run again later."
      />
    );
  }

  if (!data) {
    return (
      <EmptyState
        title="No provenance yet."
        description="Provenance is recorded once the submission writes hit the catalog."
      />
    );
  }

  const inputHashes = data.input_artifact_hashes ?? [];
  const outputArtifacts = data.output_artifacts ?? [];
  // A fan-out port's artifacts are index-paired with fan-out children,
  // so EVERY row of a multi-artifact port shows its ordinal — including
  // [0], which would otherwise be indistinguishable from a
  // single-artifact port.
  const portCounts = new Map<string, number>();
  for (const artifact of outputArtifacts) {
    portCounts.set(artifact.port, (portCounts.get(artifact.port) ?? 0) + 1);
  }

  return (
    <Card as="dl" className="grid grid-cols-1 gap-x-8 gap-y-3 p-card sm:grid-cols-[180px_1fr]">
      <ProvenanceRow label="Manifest" value={data.manifest_name} />
      <ProvenanceRow label="Manifest version" value={data.manifest_version} mono />
      <ProvenanceRow label="Container digest" value={data.container_digest} mono truncate />
      <ProvenanceRow label="Actual digest" value={data.container_digest_actual} mono truncate />
      <ProvenanceRow label="Provenance source" value={data.provenance_source} mono />
      <ProvenanceRow label="Attestation" value={<AttestationBadge value={data.attestation} />} />
      <ProvenanceRow label="Git SHA" value={data.git_sha} mono truncate />
      <ProvenanceRow label="Parameter hash" value={data.parameter_hash} mono truncate />
      <ProvenanceRow label="Cache key" value={data.cache_key} mono truncate />
      <ProvenanceRow
        label="Input artifacts"
        value={
          inputHashes.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {inputHashes.map((hash, i) => (
                <li key={`${hash}-${i}`} className="truncate font-mono text-[12px]" title={hash}>
                  {hash}
                </li>
              ))}
            </ul>
          ) : null
        }
      />
      <ProvenanceRow
        label="Output artifacts"
        value={
          outputArtifacts.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {outputArtifacts.map((artifact) => (
                <li
                  key={`${artifact.port}-${artifact.ordinal}`}
                  className="flex items-baseline gap-2"
                >
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {artifact.port}
                    {(portCounts.get(artifact.port) ?? 0) > 1 ? `[${artifact.ordinal}]` : ""}
                  </span>
                  <span className="truncate font-mono text-[12px]" title={artifact.content_hash}>
                    {artifact.content_hash}
                  </span>
                </li>
              ))}
            </ul>
          ) : null
        }
      />
      {data.parameter_values && (
        <ProvenanceRow
          label="Parameter values"
          value={
            <pre className="overflow-x-auto rounded bg-surface-2 px-2 py-1 text-[11px]">
              {JSON.stringify(data.parameter_values, null, 2)}
            </pre>
          }
        />
      )}
    </Card>
  );
}

interface RowProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  truncate?: boolean;
}

function ProvenanceRow({ label, value, mono, truncate }: RowProps): JSX.Element {
  return (
    <>
      <dt className="label-micro text-muted-foreground">{label}</dt>
      <dd
        className={[
          // `min-w-0` is #982's rule in the OTHER axis, and this row is where
          // it was missing. A grid item defaults to `min-width: auto` — it
          // refuses to be narrower than its own min-content — and this cell's
          // content is a 71-character sha256 in a monospace face with no break
          // opportunity in it. So the `1fr` track was never 1fr: it grew to
          // the hash, pushed the card past the content area, and the artifact
          // rows (whose `truncate` sits on a nested `<li>`, not on this
          // element) had nothing to clip them.
          //
          // Measured at 1024px before this: the tab overflowed `<main>` by
          // 21px. The card CLIPS, so the page did not scroll and nothing
          // looked wrong — the hashes were simply cut off at a width nobody
          // chose. `tests/e2e/repaint_overflow.spec.ts` measures it now.
          "min-w-0 text-sm text-foreground",
          mono ? "font-mono text-[12px]" : "",
          truncate ? "truncate" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        title={typeof value === "string" && value !== "" ? value : undefined}
      >
        {/* Empty string is as unrenderable as null — a blank row is
            exactly what the em-dash contract rules out. */}
        {value == null || value === "" ? <span className="text-muted-foreground">—</span> : value}
      </dd>
    </>
  );
}
